import { Injectable } from '@angular/core';
import { format, formatDistance, parseISO } from 'date-fns';
import { map, switchMap } from 'rxjs';
import {
  iModuleResult,
  iModuleResultDto,
} from 'src/app/helpers/Interfaces/modules';
import {
  iPaginationRequest,
  iPaginationResult,
} from 'src/app/helpers/Interfaces/pagination';
import { BaseService } from 'src/app/helpers/base.service';
import { environment } from 'src/environments/environment.development';
import { CustomerService } from '../../lead-konnekt/customer/customer.service';

@Injectable({
  providedIn: 'root',
})
export class ModulesService extends BaseService {
  private _getAllModules = `${environment.base_url}/api/Module/GetAllWithRelationship`;
  private _getAllModules1 = `${environment.base_url}/api/Module/GetAllWithRelationshipWithFilteration`;
  private _getModules = `${environment.base_url}/api/Module/GetAll`;
  private _getModulesByProject = `${environment.base_url}/api/Module/GetByProject`;
  private _getModuleCustomers = `${environment.base_url}/api/ModuleCustomers/GetByModuleId`;
  private _addModuleCustomer = `${environment.base_url}/api/ModuleCustomers/Add`;
  private _removeModuleCustomer = `${environment.base_url}/api/ModuleCustomers/RemoveNew`;
  private _addModule = `${environment.base_url}/api/Module/Add`;
  private _addModuleFiles = `${environment.base_url}/api/ModuleFiles/AddRangeFiles`;
  private _updateModule = `${environment.base_url}/api/Module/Update`;
  private _removeModule = `${environment.base_url}/api/Module/Remove`;
  private _removeModuleFIle = `${environment.base_url}/api/ModuleFiles/Remove`;
  constructor(private _customerServ: CustomerService) {
    super();
  }

  private processFiles(files: any[]): any[] {
    return files.map((file) => ({
      ...file,
      name: file.fileName ?? file.name,
      size: `(${((file.fileSize ?? file.size) / 1024)
        .toFixed()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} KB)`,
    }));
  }

  private processModule(res: iModuleResult): iModuleResultDto {
    const module: iModuleResultDto = {
      ...res,
      des: null,
      createdAt: null,
    };
    module.des = `${module.description || ''}${module.description && module.body ? ' - ' : ''
      }${module.body || ''}`;
    module.createdAt = formatDistance(
      parseISO(res.createdDate as string),
      new Date(),
      {
        addSuffix: true,
        includeSeconds: true,
      }
    );
    module.createdDate = format(
      parseISO(res.createdDate as string),
      'MMM do, yyyy'
    );
    module.files = this.processFiles(module.files);
    return module;
  }

  public GetAllModules() {
    return this._http
      .get<any>(this._getAllModules, { responseType: 'json' })
      .pipe(
        map((modules) =>
          modules.map((module: any) => this.processModule(module)).reverse()
        )
      );
  }
  public GetAllModules1(payload: iPaginationRequest) {
    return this._http
      .post<iPaginationResult<iModuleResult>>(this._getAllModules1, payload, {
        responseType: 'json',
      })
      .pipe(
        map((res) => ({
          ...res,
          data: res.data.map((module: any) => this.processModule(module)),
        }))
      );
  }
  public GetModules(id?: string) {
    return this._http
      .get<any>(
        id ? `${this._getModulesByProject}?Id=${id}` : this._getModules,
        { responseType: 'json' }
      )
      .pipe(
        map((modules) =>
          modules.map((module: any) => this.processModule(module)).reverse()
        )
      );
  }
  private ShapePayload(data: any) {
    const { title, description, projectId, body, etr, startDate, endDate } = data;

    const toIso = (value: any) =>
      value ? (typeof value === 'string' && value.includes('T') ? value : new Date(value).toISOString()) : null;

    return {
      title,
      description,
      projectId,
      body,
      etr: `${etr}`,
      startDate: toIso(startDate),
      endDate: toIso(endDate),
    } as any;
  }
  public AddModule(data: any) {
    const payload = this.ShapePayload(data);
    return this._http
      .post<any>(this._addModule, payload, {
        responseType: 'json',
      })
      .pipe(map((module) => this.processModule(module)));
  }

  public UpdateModule(data: any) {
    const payload = this.ShapePayload(data);
    payload.id = data.id;
    return this._http
      .put<any>(this._updateModule, payload, {
        responseType: 'json',
      })
      .pipe(map((module) => this.processModule(module)));
  }

  public AddModuleFiles(Files: FormData) {
    return this._http
      .post<any>(`${this._addModuleFiles}`, Files, {
        responseType: 'json',
      })
      .pipe(map((files) => this.processFiles(files)));
  }

  public DeleteModule(id: any) {
    return this._http.delete(`${this._removeModule}?id=${id}`);
  }
  public DeleteModuleFile(id: string) {
    return this._http.delete(`${this._removeModuleFIle}?id=${id}`);
  }
  private customers: any[] = [];

  public HandleModuleCustomer(
    customerId: string,
    moduleId: string,
    include: boolean
  ) {
    if (!include)
      return this._http.post(
        this._addModuleCustomer,
        { customerId, moduleId },
        { responseType: 'json' }
      );
    return this._http.delete(
      `${this._removeModuleCustomer}?ModuleId=${moduleId}&CustomerId=${customerId}`,
      { responseType: 'json' }
    );
  }

  public GetModuleCustomers(moduleId: string) {
    if (this.customers.length > 0) {
      return this.getModuleCustomersFromCachedData(moduleId);
    } else {
      return this.getModuleCustomersFromApi(moduleId);
    }
  }
  private getModuleCustomersFromCachedData(moduleId: string) {
    return this._http
      .get<any[]>(`${this._getModuleCustomers}?id=${moduleId}`)
      .pipe(
        map((moduleCustomers) => {
          const yFilter = moduleCustomers.map((itemY) => itemY.customerId);
          const filteredX = this.customers.filter(
            (itemX) => !yFilter.includes(itemX.id)
          );

          const response = this.customers.map((e) => ({
            ...e,
            include: !filteredX.some((x) => x.id === e.id),
            loading: false,
          }));

          return response;
        })
      );
  }
  private getModuleCustomersFromApi(moduleId: string) {
    return this._http
      .get<any[]>(`${this._getModuleCustomers}?id=${moduleId}`)
      .pipe(
        switchMap((moduleCustomers) => {
          return this._customerServ
            .GetAllCustomers({ skipPagination: true })
            .pipe(
              map((customers) => {
                this.customers = customers.data;
                return { moduleCustomers, customers };
              })
            );
        }),
        map(({ moduleCustomers, customers }) => {
          const yFilter = moduleCustomers.map((itemY) => itemY.customerId);
          const filteredX = customers.data.filter(
            (itemX) => !yFilter.includes(itemX.id)
          );

          const response = customers.data.map((e) => ({
            ...e,
            include: !filteredX.some((x) => x.id === e.id),
            loading: false,
          }));

          return response;
        })
      );
  }
}
